import React, { useState } from 'react';
import { FiX } from 'react-icons/fi';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SettingsModal: React.FC<SettingsModalProps> = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState<'datetime' | 'language' | 'password'>('datetime');
  const [loading, setLoading] = useState(false);

  // Состояния для настроек даты и времени
  const [dateFormat, setDateFormat] = useState('DD.MM.YYYY');
  const [timeFormat, setTimeFormat] = useState('24');

  // Состояния для языка
  const [language, setLanguage] = useState('ru');

  // Состояния для смены пароля
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const dateFormats = [
    { value: 'DD.MM.YYYY', label: '31.12.2023' },
    { value: 'MM/DD/YYYY', label: '12/31/2023' },
    { value: 'YYYY-MM-DD', label: '2023-12-31' },
    { value: 'DD MMM YYYY', label: '31 дек 2023' },
  ];

  const timeFormats = [
    { value: '24', label: '24-часовой (14:30)' },
    { value: '12', label: '12-часовой (2:30 PM)' },
  ];

  const languages = [
    { code: 'ru', name: 'Русский' },
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Español' },
    { code: 'fr', name: 'Français' },
    { code: 'de', name: 'Deutsch' },
  ];

  const handleSave = async () => {
    setLoading(true);
    try {
      // TODO: Реализовать сохранение настроек через API
      console.log('Saving settings:', {
        activeTab,
        dateFormat,
        timeFormat,
        language,
        oldPassword,
        newPassword,
        confirmPassword,
      });
      
      // Имитация задержки API
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Сбросить пароли после сохранения
      if (activeTab === 'password') {
        setOldPassword('');
        setNewPassword('');
        setConfirmPassword('');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      // Сбросить пароли при закрытии
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
        {/* Заголовок */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Настройки</h2>
          <button
            onClick={handleClose}
            disabled={loading}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <FiX className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="flex">
          {/* Боковое меню */}
          <div className="w-48 border-r border-gray-200">
            <nav className="p-4 space-y-1">
              <button
                onClick={() => setActiveTab('datetime')}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === 'datetime'
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                Дата и время
              </button>
              <button
                onClick={() => setActiveTab('language')}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === 'language'
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                Язык
              </button>
              <button
                onClick={() => setActiveTab('password')}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === 'password'
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                Сменить пароль
              </button>
            </nav>
          </div>

          {/* Содержимое */}
          <div className="flex-1 p-6 overflow-y-auto max-h-[60vh]">
            {activeTab === 'datetime' && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">Формат даты и времени</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Формат даты
                  </label>
                  <div className="space-y-2">
                    {dateFormats.map((format) => (
                      <label key={format.value} className="flex items-center">
                        <input
                          type="radio"
                          name="dateFormat"
                          value={format.value}
                          checked={dateFormat === format.value}
                          onChange={(e) => setDateFormat(e.target.value)}
                          className="mr-3"
                        />
                        <span className="text-sm text-gray-700">{format.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Формат времени
                  </label>
                  <div className="space-y-2">
                    {timeFormats.map((format) => (
                      <label key={format.value} className="flex items-center">
                        <input
                          type="radio"
                          name="timeFormat"
                          value={format.value}
                          checked={timeFormat === format.value}
                          onChange={(e) => setTimeFormat(e.target.value)}
                          className="mr-3"
                        />
                        <span className="text-sm text-gray-700">{format.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'language' && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">Язык интерфейса</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Выберите язык
                  </label>
                  <select
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {languages.map((lang) => (
                      <option key={lang.code} value={lang.code}>
                        {lang.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {activeTab === 'password' && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">Смена пароля</h3>
                
                <div>
                  <label htmlFor="oldPassword" className="block text-sm font-medium text-gray-700 mb-2">
                    Старый пароль
                  </label>
                  <input
                    type="password"
                    id="oldPassword"
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                    Новый пароль
                  </label>
                  <input
                    type="password"
                    id="newPassword"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                    Повторить новый пароль
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Подвал */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            Отмена
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
          >
            {loading && (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            )}
            <span>{loading ? 'Сохранение...' : 'Сохранить'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;
